import Vue from 'vue'
import { scopeOf } from '@/config/configSchema'
import { scheduleFlush, flushNow, flushPending } from './config.persistence'

/**
 * Config Bus — actions (Sprint 16, epic REFACTOR-030).
 *
 * API namespaced (`config/...`):
 *  - load         : carica user+company dal backend (blob grezzo; i default si
 *                   applicano in lettura nel getter `value`)
 *  - commitChange : scrittura ottimistica granulare + persist batch/rollback
 *  - reset        : azzera la config utente (backend) → blob vuoto
 *  - flush        : forza il flush pendente (es. prima di unload)
 *
 * Sprint 19: ritirata `saveLegacy` (e lo shim `geofleet/saveConfig` che la usava) —
 * tutti i writer passano da `commitChange`.
 *
 * Vedi `docs/architecture/config-bus.md`.
 */
const actions = {
    // Carica le config dal backend. Memorizza il blob GREZZO (nessun default cotto):
    // i default ricadono in lettura su `value(key)`, così `blob` = legacy.
    async load({ commit }, payload = {}) {
        const { companyId } = payload
        commit('SET_STATUS', { loading: true, error: null })
        // `companyId` è INFORMATIVO (state.companyId): il backend risolve l'azienda
        // dall'header X-Company (plugins/Caller.js) e il cambio azienda fa
        // location.reload() (Sprint 9) → niente indicizzazione per-company del blob.
        // Chiude il residuo REFACTOR-029 (intento chiarito); desktop e Mobile lo
        // passano uniformemente dal bootstrap (Sprint 19).
        if (companyId != null) commit('SET_COMPANY_ID', companyId)

        const apiBase = Vue.prototype.$env.GOLIA_API

        // NB: un fallimento di LOAD NON scrive su `status.error`. Quel canale è
        // riservato ai fallimenti di SAVE (ROLLBACK in persistence) che il
        // ConfigSavingIndicator traduce nel toast "modifiche annullate" — fuorviante
        // al bootstrap. Su GET fallita degradiamo silenziosamente al blob vuoto (i
        // reader ricadono sui default di schema, come il legacy `loadConfig`).
        try {
            const res = await Vue.prototype.$fetch(apiBase + '/store?type=user&key=geofleet')
            const result = await res.json()
            commit('HYDRATE', { scope: 'user', blob: result?.data || {} })
        } catch (error) {
            commit('HYDRATE', { scope: 'user', blob: {} })
            console.warn('[config/load] user blob non caricato, fallback ai default:', error)
        }

        try {
            const res = await Vue.prototype.$fetch(apiBase + '/store?type=company&key=geofleet')
            const result = await res.json()
            commit('HYDRATE', { scope: 'company', blob: result?.data || {} })
        } catch (error) {
            commit('HYDRATE', { scope: 'company', blob: {} })
            console.warn('[config/load] company blob non caricato, fallback ai default:', error)
        }

        commit('SET_STATUS', { loading: false })
    },

    // Scrittura ottimistica di una singola chiave: UI istantanea + persist
    // debounced (coalescing) + rollback su fallimento (gestito in persistence).
    commitChange({ commit, state }, { key, value, scope }) {
        const sc = scope || scopeOf(key)
        const target = sc === 'company' ? state.company : state.user
        const prev = Object.prototype.hasOwnProperty.call(target, key) ? target[key] : undefined

        commit('MARK_DIRTY', { scope: sc, key, prev })
        commit('SET_VALUE', { scope: sc, key, value })
        commit('SET_STATUS', { saving: true, error: null })
        scheduleFlush({ commit, state }, sc)
    },

    async resetPoiFilters({ dispatch, state }) {
        const activeFilters = state.user.activeFilters
        if (!activeFilters || typeof activeFilters !== 'object') return

        const hasPoiFilter =
            (activeFilters.poi_id != null && activeFilters.poi_id !== '') ||
            (activeFilters.poi_category_id != null && activeFilters.poi_category_id !== '')
        if (!hasPoiFilter) return

        dispatch('commitChange', {
            key: 'activeFilters',
            value: {
                ...activeFilters,
                poi_id: null,
                poi_category_id: null
            },
            scope: 'user'
        })
        await dispatch('flush', 'user')
    },

    // Forza il flush pendente. SENZA scope → tutti gli scope con writes in volo
    // (user E company): è la forma usata dagli hook di unload (GeoFleet.vue), così
    // anche un commitChange company (es. poiSettings) non resta nel debounce.
    // CON scope → forza solo quello.
    flush({ commit, state }, scope) {
        if (scope) return flushNow({ commit, state }, scope)
        return flushPending({ commit, state })
    },

    // Reset della config utente: POST blob vuoto, poi blob vuoto in locale (i
    // consumer ricadono sui default come nel legacy `setConfig({})`).
    async reset({ commit }) {
        commit('SET_STATUS', { saving: true, error: null })
        try {
            const res = await Vue.prototype.$fetch(Vue.prototype.$env.GOLIA_API + '/store', {
                method: 'POST',
                body: JSON.stringify({ type: 'user', key: 'geofleet', data: {} })
            })
            await res.json()
            commit('HYDRATE', { scope: 'user', blob: {} })
            commit('CLEAR_DIRTY', 'user')
            commit('SET_STATUS', { saving: false, error: null })
            return true
        } catch (error) {
            // Su fallimento `status.error` arma il toast del ConfigSavingIndicator: il
            // caller NON deve mostrare il proprio toast di successo. Ritorna false così
            // `resetConfig` (SettingsPanel) evita il doppio toast contraddittorio.
            commit('SET_STATUS', { saving: false, error })
            return false
        }
    }
}

export default actions
